import { ComponentPropsWithoutRef, useEffect, useRef, useState } from 'react';
import { IHolder } from '@/request/token/token.response.interface';
import NoData from '@/widget/NoData';
import tokenApiService from '@/request/token/token.api.service';
import HolderHead from './components/HolderHead';
import HolderLoading from './components/HolderLoading';
import HolderRow from './components/HolderRow';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTradeStore } from '../../store';
import { useDynamicHeight } from '@/hooks/base/useDynamicHeight';
import { useBoomWallet } from '@boomeme/wallet-sdk';

interface IProps extends Omit<ComponentPropsWithoutRef<'div'>, 'type'> {}
export default (_: IProps) => {
  const { chain } = useBoomWallet();
  const { tokenInfo } = useTradeStore();
  const [loading, setLoading] = useState(false);
  const [holders, setHolders] = useState<IHolder[]>([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (tokenInfo?.address) {
          setLoading(true);
          const res = await tokenApiService.getTokenHolders({ tokenAddress: tokenInfo.address });
          if (res.code == 0) {
            setLoading(false);
            setHolders(res.data.items);
          }
        }
      } catch (error) {
        setHolders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tokenInfo?.address, chain]);

  const parentRef = useRef<HTMLDivElement>(null);
  const viewHeight = useDynamicHeight({ customHeight: 100 });

  const rowVirtualizer = useVirtualizer({
    count: holders.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72,
    overscan: 5,
  });

  return (
    <div>
      <HolderHead />
      {loading ? (
        <HolderLoading />
      ) : holders.length > 0 ? (
        <div
          ref={parentRef}
          style={{
            height: viewHeight + 'px',
            overflow: 'auto',
          }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => (
              <div
                key={virtualRow.index}
                style={{
                  position: 'absolute',
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <HolderRow holder={holders[virtualRow.index]} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <NoData />
      )}
    </div>
  );
};
